// Module-level Socket.IO singleton for the operator console per
// docs/ui/operator-ui-tds.md §5.1 + §5.4. Scoped to the `/cases` namespace.
//
// Connection lifecycle is mirrored into the Zustand connection store
// (src/state/connection.store.ts). Event payload handling — the central
// applyEvent merge per TDS §5.3 — is NEX-149's deliverable; this module
// installs only the lifecycle hooks.
//
// The socket is module-scoped (not a hook) so non-React code (force-logout's
// WS-close registration, future Cmd+K palette, idle monitor) can interact
// with it without depending on React context.

import { io, type Socket } from 'socket.io-client';
import { getAccessToken } from '@/auth/token-holder';
import { getSessionUrn } from '@/auth/session-urn';
import { forceLogout } from '@/auth/force-logout';
import { useConnectionStore } from '@/state/connection.store';
import { queryClient } from '@/lib/query-client';

const NAMESPACE = '/cases';

let socket: Socket | null = null;

// Rooms this tab is subscribed to. Socket.IO rooms live on the SERVER socket,
// so a reconnect (BFF restart, network blip) lands on a fresh server socket that
// is in NO room — every push for the open detail page would silently stop. We
// remember what the page joined and re-join on every (re)connect.
// Two ways in: a plain `join`/`leave` (idempotent, last call wins — several hooks
// re-join without ever leaving), and a ref-counted `hold` whose release is the only
// thing that drops it. A plain leave never drops a room someone still holds: the
// roster PNR drawer's `leaveCase` must not silence Operar's case rooms under it.
const joinedRooms = new Map<string, { event: string; args: string[]; plain: boolean; holds: number }>();
let everConnected = false;

function roomKey(event: string, arg?: string): string {
  return arg === undefined ? event : `${event}:${arg}`;
}

function roomOf(event: string, arg?: string) {
  const key = roomKey(event, arg);
  let room = joinedRooms.get(key);
  if (!room) {
    room = { event, args: arg === undefined ? [] : [arg], plain: false, holds: 0 };
    joinedRooms.set(key, room);
  }
  return room;
}

function join(event: string, arg?: string): void {
  roomOf(event, arg).plain = true;
  socket?.emit(event, ...(arg === undefined ? [] : [arg]));
}

function leave(joinEvent: string, leaveEvent: string, arg?: string): void {
  const room = joinedRooms.get(roomKey(joinEvent, arg));
  if (room && room.holds > 0) {
    room.plain = false;
    return;
  }
  joinedRooms.delete(roomKey(joinEvent, arg));
  socket?.emit(leaveEvent, ...(arg === undefined ? [] : [arg]));
}

/** Ref-counted join; the returned release leaves only when nobody else is in. */
function hold(joinEvent: string, leaveEvent: string, arg?: string): () => void {
  const room = roomOf(joinEvent, arg);
  room.holds += 1;
  if (room.holds === 1 && !room.plain) socket?.emit(joinEvent, ...room.args);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    room.holds -= 1;
    if (room.holds > 0 || room.plain) return;
    joinedRooms.delete(roomKey(joinEvent, arg));
    socket?.emit(leaveEvent, ...room.args);
  };
}

/** Re-subscribe every remembered room on the (new) server socket. */
function rejoinRooms(): void {
  for (const { event, args } of joinedRooms.values()) socket?.emit(event, ...args);
}

// React layer (inside QueryClientProvider) registers the event handler so the
// socket can dispatch incoming events into the TanStack Query cache via
// applyEvent(qc, event). NEX-149's central merge consumes this.
type WsEventHandler = (event: { type: string; [k: string]: unknown }) => void;
let eventHandler: WsEventHandler | null = null;

export function setWsEventHandler(handler: WsEventHandler | null): void {
  eventHandler = handler;
}

export function getSocket(): Socket | null {
  return socket;
}

export function connectSocket(): Socket {
  if (socket) return socket;
  // Read env lazily so tests can stubEnv() before calling connectSocket().
  const configuredBaseUrl = import.meta.env.VITE_WS_BASE_URL ?? '';
  // Published tenant images run behind the VM's same-origin proxy. Resolve at
  // runtime so changing a hostname never requires rebuilding the image.
  const wsBaseUrl = configuredBaseUrl === 'same-origin'
    ? window.location.origin
    : configuredBaseUrl;
  if (!wsBaseUrl) {
    throw new Error(
      'VITE_WS_BASE_URL is required to connect Socket.IO. See docs/ui/operator-ui-tds.md §12.2.',
    );
  }

  const next = io(wsBaseUrl + NAMESPACE, {
    transports: ['websocket'],
    // Async auth callback re-runs on every reconnect — silently-refreshed
    // tokens propagate naturally per TDS §5.1.
    auth: async (
      cb: (data: { token: string; sessionUrn: string; devOperatorOverride?: string }) => void,
    ) => {
      let devOperatorOverride: string | undefined;
      try {
        const stored = window.sessionStorage.getItem('nexa.devOperatorOverride');
        if (stored) devOperatorOverride = stored;
      } catch {
        // sessionStorage unavailable — silently fall through.
      }
      // Per-tab session URN (TDS §4) — same value the HTTP client sends as
      // X-Session-Urn, so acquire (HTTP) and disconnect-release (this socket)
      // key the lock index identically and per-tab isolation holds.
      const sessionUrn = getSessionUrn();
      try {
        const token = await getAccessToken();
        cb({ token, sessionUrn, ...(devOperatorOverride ? { devOperatorOverride } : {}) });
      } catch {
        cb({ token: '', sessionUrn, ...(devOperatorOverride ? { devOperatorOverride } : {}) });
      }
    },
    reconnection: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
  });

  // Lifecycle → Zustand store.
  next.on('connect', () => {
    const store = useConnectionStore.getState();
    store.setConnected(true);
    store.resetReconnectAttempts();
    store.markUpdate();
    // Fresh server socket → re-join the rooms the open pages depend on.
    rejoinRooms();
    // A RE-connect means pushes were missed while we were away: every cached
    // read may be stale (a run may have finished, facts moved). Resync once —
    // this is the push-driven model healing a gap, not a poll.
    if (everConnected) void queryClient.invalidateQueries();
    everConnected = true;
  });

  next.on('disconnect', (reason: string) => {
    const store = useConnectionStore.getState();
    store.setConnected(false);
    store.setError(reason);
  });

  next.io.on('reconnect_attempt', () => {
    useConnectionStore.getState().incrementReconnectAttempts();
  });

  next.io.on('reconnect', () => {
    useConnectionStore.getState().resetReconnectAttempts();
  });

  next.on('connect_error', (err: Error) => {
    useConnectionStore.getState().setError(err.message);
  });

  // Catch-all dispatch into the central applyEvent merge (NEX-149). The BFF
  // emits one Socket.IO event per type with the rest of the WsEvent shape in
  // the payload — this onAny call reconstructs `{ type: eventName, ...payload }`
  // and hands it to the registered React-side handler.
  next.onAny((eventName: string, payload?: Record<string, unknown>) => {
    if (!eventHandler) return;
    if (typeof eventName !== 'string' || eventName.startsWith('connect') || eventName === 'disconnect') {
      return;
    }
    eventHandler({ type: eventName, ...(payload ?? {}) });
  });

  // BFF closes the socket with code 4401 on token rejection per TDS §5.1.
  // The Engine.IO 'close' event surfaces the close code via the description
  // string ("transport close: code=4401" depending on adapter); we accept
  // any signal mentioning 4401 as ws-token-rejected.
  next.io.engine?.on('close', (reason: string) => {
    if (typeof reason === 'string' && reason.includes('4401')) {
      void forceLogout({ reason: 'ws-token-rejected' });
    }
  });

  socket = next;
  return next;
}

export async function disconnectSocket(): Promise<void> {
  const current = socket;
  // Explicit client-side teardown (logout) — forget the rooms too; the pages
  // that joined them are going away with the session.
  joinedRooms.clear();
  everConnected = false;
  if (!current) return;
  socket = null;

  await new Promise<void>((resolve) => {
    if (!current.connected) {
      resolve();
      return;
    }
    const done = (): void => {
      current.off('disconnect', done);
      resolve();
    };
    current.once('disconnect', done);
    current.disconnect();
    // Safety net: socket.io's disconnect is synchronous in v4 but the event
    // can be batched. Resolve after 200 ms regardless so force-logout never
    // blocks longer than the TDS §7 ~200 ms WS-close budget.
    setTimeout(resolve, 200);
  });
}

// Per-case room subscription helpers per TDS §5.4. Called by case-detail
// route mount/unmount; the dashboard subscribes to a separate cluster-wide
// channel and does NOT use these helpers.
export function joinCase(caseUrn: string): void {
  join('join:case', caseUrn);
}

export function leaveCase(caseUrn: string): void {
  leave('join:case', 'leave:case', caseUrn);
}

/** A ref-counted case room (see `hold`): call the returned function to let go. */
export function holdCase(caseUrn: string): () => void {
  return hold('join:case', 'leave:case', caseUrn);
}

// Per-disruption room subscription (NEX-293). The control center joins the
// `disruption:<urn>` room on detail mount; the BFF fans out live progress +
// run-state (`DisruptionProgressChanged`) to that room, so the Progress tab and
// the bulk bar update WITHOUT any `setInterval` poll. No-op when not connected.
export function joinDisruption(disruptionUrn: string): void {
  join('join:disruption', disruptionUrn);
}

export function leaveDisruption(disruptionUrn: string): void {
  leave('join:disruption', 'leave:disruption', disruptionUrn);
}

// Global provider-status room (D). The dedicated provider-status page joins on
// mount; the BFF fans out live per-vendor health (`ProviderStatusChanged`) so the
// page updates over the WS, no poll. No-op when not connected.
export function joinProviders(): void {
  join('join:providers');
}

export function leaveProviders(): void {
  leave('join:providers', 'leave:providers');
}

// Cluster-wide flights-monitor room (NEX-343 / F5). The /flights monitor joins on
// mount; the BFF (FlightsGateway, same `/cases` namespace) fans out live
// `flight.status-changed` deltas to this room so the list updates over the WS —
// this REPLACES the F4 30s refetchInterval poll. No-op when not connected.
export function joinFlights(): void {
  join('join:flights');
}

export function leaveFlights(): void {
  leave('join:flights', 'leave:flights');
}
